import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Apple, CheckCircle, Utensils, TrendingUp } from 'lucide-react';
import { siteData } from '../data/mock';

const Nutrition = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animated').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const nutritionFeatures = [
    {
      title: "Plan alimentaire personnalisé",
      description: "Un programme nutrition adapté à ton mode de vie, tes goûts et tes objectifs.",
      icon: Apple
    },
    {
      title: "Suivi régulier",
      description: "Ajustements et conseils tout au long de ton parcours pour optimiser tes résultats.",
      icon: TrendingUp
    },
    {
      title: "Recettes et conseils",
      description: "Des idées de repas simples et savoureux qui s'intègrent facilement à ton quotidien.",
      icon: Utensils
    }
  ];

  const benefits = [
    "Perte de poids durable",
    "Gain d'énergie au quotidien",
    "Amélioration des performances sportives",
    "Meilleure récupération",
    "Relation saine avec l'alimentation",
    "Résultats visibles rapidement"
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-page)] pt-20">
      {/* Hero */}
      <section className="section-padding bg-[var(--brand-dark)] text-white">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animated">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Suivi Nutritionnel Complet
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Une alimentation adaptée est la clé pour atteindre tes objectifs. Je t'accompagne pour créer de nouvelles habitudes durables.
              </p>
              <Link to="/contact" className="btn-primary">
                Commencer mon suivi
              </Link>
            </div>
            <div className="animated">
              <img
                src={siteData.images.nutrition}
                alt="Suivi nutritionnel"
                className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Nutrition */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-16 animated">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
              Pourquoi la nutrition est essentielle ?
            </h2>
            <p className="text-xl text-[var(--text-secondary)] max-w-3xl mx-auto">
              L'entraînement représente 30% de tes résultats. Les 70% restants viennent de ton alimentation. 
              Sans un plan nutritionnel adapté, tu limites ton potentiel de transformation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 animated"
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <CheckCircle className="text-[var(--brand-primary)] flex-shrink-0 mt-1" size={24} />
                <span className="text-lg text-[var(--text-secondary)]">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animated">
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
              Comment je t'accompagne
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {nutritionFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="network-card animated text-center"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-16 h-16 rounded-full bg-[var(--brand-primary)] flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-semibold text-[var(--text-primary)] mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-[var(--text-secondary)]">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="section-padding">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animated">
              <img
                src={siteData.images.transformation}
                alt="Transformation nutrition"
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
            <div className="animated">
              <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-6">
                Ma philosophie nutrition
              </h2>
              <p className="text-xl text-[var(--text-secondary)] mb-6">
                Pas de régime strict ni de privation. Mon approche se base sur l'équilibre, la flexibilité et la création d'habitudes durables.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  {
                    title: "Adapté à ton mode de vie",
                    desc: "Des solutions pratiques qui s'intègrent à ton quotidien"
                  },
                  {
                    title: "Sans restriction extrême",
                    desc: "Apprends à manger équilibré sans te priver"
                  },
                  {
                    title: "Éducation nutritionnelle",
                    desc: "Comprends ce que tu manges pour être autonome"
                  }
                ].map((item, idx) => (
                  <div key={idx} className="border-l-4 border-[var(--brand-primary)] pl-4">
                    <h4 className="font-semibold text-lg text-[var(--text-primary)]">{item.title}</h4>
                    <p className="text-[var(--text-secondary)]">{item.desc}</p>
                  </div>
                ))}
              </div>
              <Link to="/contact" className="btn-primary">
                Commencer mon suivi nutrition
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Combined Offer */}
      <section className="section-padding bg-[var(--bg-subtle)]">
        <div className="container-max">
          <div className="max-w-4xl mx-auto network-card text-center animated">
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)] mb-4">
              Coaching Sport + Nutrition
            </h2>
            <p className="text-xl text-[var(--text-secondary)] mb-6">
              Pour des résultats optimaux, combine ton coaching sportif avec un suivi nutritionnel complet. 
              C'est l'accompagnement le plus efficace pour une transformation durable.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <CheckCircle className="text-[var(--brand-primary)]" size={24} />
              <span className="text-lg text-[var(--text-secondary)]">Approche globale</span>
              <CheckCircle className="text-[var(--brand-primary)]" size={24} />
              <span className="text-lg text-[var(--text-secondary)]">Résultats maximisés</span>
              <CheckCircle className="text-[var(--brand-primary)]" size={24} />
              <span className="text-lg text-[var(--text-secondary)]">Suivi via application</span>
            </div>
            <div className="mt-8">
              <Link to="/contact" className="btn-primary">
                Découvrir l'offre complète
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[var(--brand-dark)] text-white">
        <div className="container-max text-center">
          <div className="max-w-3xl mx-auto animated">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Transforme ton alimentation
            </h2>
            <p className="text-xl mb-8 text-gray-300">
              Commence dès aujourd'hui à créer des habitudes alimentaires qui soutiennent tes objectifs
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact" className="btn-primary">
                Prendre rendez-vous
              </Link>
              <Link to="/coaching" className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-[var(--brand-dark)]">
                Voir le coaching sportif
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Nutrition;
